import { useEffect, useState } from 'react'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import type { Appearance } from '@stripe/stripe-js'
import { getStripe } from '../../lib/stripe'
import { ClubButton } from './ClubButton'

/**
 * The club's card form — the second and last file importing
 * @stripe/react-stripe-js (the first is components/wallet/TopUpPaymentForm,
 * which is the frozen /steam kit's).
 *
 * NOT exported from components/golf/index.ts, on purpose. That barrel is
 * imported by every club screen and by clubPages.test.tsx, so exporting this
 * would pull @stripe/* into all of their module graphs. The one screen that
 * needs it imports it by path.
 *
 * The PAN never enters our DOM — the Payment Element is a Stripe-hosted
 * iframe, so this keeps the account at SAQ-A exactly as hosted Checkout did.
 * What changes is that the chrome, the copy and the coupon field are ours.
 */

// Stripe paints inside its own iframe, out of reach of our stylesheet, and its
// appearance API takes plain CSS colours only — so these are the golf tokens
// from entrypoints/fireside.css transcribed by hand. Keep them in step.
//
// `flat` on clubhouse paper, NOT the wallet form's `night`: the two design
// systems share no tokens, and the firelight object on this cream ground would
// be unreadable. Reaching for it is the exact ui/-into-golf/ leak the barrel
// comment forbids.
const appearance: Appearance = {
  theme: 'flat',
  variables: {
    colorPrimary: '#2c5a41', // fairway
    colorBackground: '#fbf7ee', // parchment
    colorText: '#2a251c', // ink
    colorTextSecondary: '#6b6353', // ink-soft
    colorDanger: '#b0392c', // flag
    borderRadius: '3px', // radius-club — the club is square
    fontFamily: '"Hanken Grotesk Variable", ui-sans-serif, system-ui, sans-serif',
  },
}

interface DuesPaymentFormProps {
  clientSecret: string
  /** Absolute clubReturnUrl(subscriptionId) — the /join/welcome 3DS landing. */
  returnUrl: string
  /**
   * Fired on IN-PLACE confirmation. This only advances the UI: the standing
   * becomes real when the club records it, which happens on arrival at
   * /join/welcome (or from the webhook), never because the browser said so.
   */
  onConfirmed: () => void
  onCancel: () => void
}

export function DuesPaymentForm({
  clientSecret,
  returnUrl,
  onConfirmed,
  onCancel,
}: DuesPaymentFormProps) {
  // getStripe() resolves null when no publishable key is configured. Say so
  // rather than rendering an Elements form that never loads.
  const [unavailable, setUnavailable] = useState(false)

  useEffect(() => {
    let active = true
    getStripe().then(
      (stripe) => {
        if (active && !stripe) setUnavailable(true)
      },
      () => {
        if (active) setUnavailable(true)
      },
    )
    return () => {
      active = false
    }
  }, [])

  if (unavailable) {
    return (
      <div className="space-y-5">
        <p className="text-[13.5px] text-ink-soft">
          The front desk can&rsquo;t take dues in this environment — payments aren&rsquo;t configured.
        </p>
        <ClubButton variant="ghost" size="sm" onClick={onCancel}>
          Back
        </ClubButton>
      </div>
    )
  }

  return (
    <Elements stripe={getStripe()} options={{ clientSecret, appearance }}>
      <DuesFormInner returnUrl={returnUrl} onConfirmed={onConfirmed} onCancel={onCancel} />
    </Elements>
  )
}

function DuesFormInner({ returnUrl, onConfirmed, onCancel }: Omit<DuesPaymentFormProps, 'clientSecret'>) {
  const stripe = useStripe()
  const elements = useElements()
  const [ready, setReady] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (!stripe || !elements || submitting) return
    setSubmitting(true)
    setError('')
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      // Stay in-page for plain cards; only redirect-based methods (a 3DS
      // challenge) leave for returnUrl.
      redirect: 'if_required',
    })
    if (result.error) {
      const { type, message } = result.error
      setError(
        (type === 'card_error' || type === 'validation_error') && message
          ? message
          : 'Something went wrong settling your dues — try again.',
      )
      setSubmitting(false)
      return
    }
    onConfirmed()
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-5" aria-label="Card details">
      {!ready && (
        <p className="py-6 text-center text-[13.5px] text-ink-soft">Opening the front desk&hellip;</p>
      )}
      <PaymentElement onReady={() => setReady(true)} />
      {error && (
        <p className="text-[13.5px] text-flag" role="alert">
          {error}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-4">
        <ClubButton
          type="submit"
          variant="flag"
          disabled={!stripe || !elements || !ready || submitting}
        >
          {submitting ? 'Settling…' : 'Settle the dues'}
        </ClubButton>
        <ClubButton type="button" variant="ghost" size="sm" onClick={onCancel} disabled={submitting}>
          Back
        </ClubButton>
      </div>
    </form>
  )
}
